// Libs
import classNames from 'classnames/bind';
import { pdf } from '@react-pdf/renderer';
import { useParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseLabel, BaseTable, ConfirmModal, Loading, UnderDevelopment } from '~/components/common';
import {
  EventDetailsQuoteDetail,
  EventTineLineQuoteDetail,
  FeesQuoteDetail,
  FormPaymentQuote,
  InformationQuote,
  NoteQuoteDetail,
  PaymentListModal,
  QuotePdf,
  TimeLineActionQuote,
  TotalInfoQuoteDetail,
} from '~/components/module/quotes';
// Others
import {
  DEFAULT_INVOICE_FILE_NAME_PDF,
  DEFAULT_QUOTE_FILE_NAME_PDF,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailQuote, sendMailQuote } from '~/thunks/quote/quoteThunk';
import {
  BusinessTypeEnum,
  DiscountTypeEnum,
  TypeInventoryGroupDay,
  TypeListInventory,
  TypeQuoteDetailEnum,
} from '~/utils/enum/common';
import {
  IPaymentQuote,
  IProductQuoteItem,
  IQueryParamQuoteDetail,
  IQuoteAddOn,
  IQuoteDetail,
  IQuoteItem,
  IQuotePdf,
  IQuoteSubRental,
} from '~/utils/interface/quotes';
import { createPayment } from '~/thunks/payment/paymentThunk';
import { IProductOrderItem } from '~/utils/interface/order';
import { IBodyUpdate, IHeaderCellType } from '~/utils/interface/common';
import { quoteActions, selectIsRefreshDetailQuote } from '~/thunks/quote/quoteSlice';
import { formatCurrencyUSD, handleCalculateDiscountedPrice } from '~/utils/helper/common';
// Styles, images, icons
import styles from './InfoQuoteTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IProductQuoteItem>[] => {
  return [
    {
      id: 'name',
      label: t('quote_detail_table_title_name'),
      render: (record: IProductQuoteItem) => (
        <Stack gap={2} direction={'row'} alignItems={'center'}>
          {record?.parentType && (
            <Typography className={cx('addOn')}>
              {record?.type === TypeListInventory.PRODUCT && record?.parentType === TypeListInventory.PACKAGE
                ? t('quote_detail_table_title_package_item')
                : t('quote_detail_table_title_add_on')}
            </Typography>
          )}

          <Stack gap={'2px'}>
            <Stack flexDirection={'row'} gap={2} alignItems={'center'}>
              <Box>{record?.name || EMPTY_VALUE}</Box>

              {record.type === TypeListInventory.PACKAGE && (
                <Box className={cx('viewBox')}>{t('form_quotes_label_package')}</Box>
              )}

              {record?.isDelete && (
                <Box className={cx('delete')}>{t('common_button_delete', { ns: 'common/base' })}</Box>
              )}
            </Stack>

            {record?.variantName && <Box className={cx('description')}>{record?.variantName}</Box>}

            {record?.description && <Box className={cx('description')}>{record?.description}</Box>}

            {/* {record?.isRequired && (
              <Box className={cx('textRequired')}>{t('common_text_required', { ns: 'common/base' })}</Box>
            )} */}
          </Stack>
        </Stack>
      ),
    },
    {
      id: 'discountRate',
      label: t('quote_detail_table_title_discount'),
      render: (record: IProductOrderItem) =>
        record?.discountType === DiscountTypeEnum.PERCENTAGE
          ? `${Number(record?.discountRate)}%`
          : formatCurrencyUSD({ value: Number(record?.discountRate) }) || EMPTY_VALUE,
    },
    {
      id: 'price',
      label: t('quote_detail_table_title_rental_price'),
      render: (record: IProductOrderItem) => {
        return (
          <Stack direction={'row'}>
            <Typography className={cx('price', record?.discountRate && 'viewPrice')}>
              {formatCurrencyUSD({ value: record?.price, showDecimal: true })}
            </Typography>

            {!!record?.discountRate && (
              <>
                <Typography className={cx('price')}>/</Typography>
                <Typography className={cx('price')}>
                  {formatCurrencyUSD({
                    value: handleCalculateDiscountedPrice(
                      record?.price,
                      Number(record?.discountRate),
                      record?.discountType
                    ),
                    showDecimal: true,
                  })}
                </Typography>
              </>
            )}
          </Stack>
        );
      },
    },
    {
      id: 'laborCost',
      label: t('quote_detail_table_title_labor_cost'),
      render: (record: IProductOrderItem) => formatCurrencyUSD({ value: record?.laborCost }) || EMPTY_VALUE,
    },
    {
      id: 'quantity',
      label: t('quote_detail_table_title_quantity'),
      render: (record: IProductOrderItem) => record?.quantity || EMPTY_VALUE,
    },
    {
      id: 'total',
      label: t('quote_detail_table_title_total'),
      render: (record: IProductOrderItem) =>
        record?.isRequired && record?.parentType === TypeListInventory.PACKAGE
          ? EMPTY_STRING
          : formatCurrencyUSD({ value: record?.total, showDecimal: true }),
    },
  ];
};

const InfoQuoteTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { id: quoteId } = useParams();
  const { t } = useTranslation(['admin/quotes', 'common/base', 'admin/order']);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshQuoteDetail = useSelector(selectIsRefreshDetailQuote);
  //#endregion Selector

  //#region Declare State
  const [detailQuote, setDetailQuote] = useState<IQuoteDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowPaymentModal, setIsShowPaymentModal] = useState<boolean>(false);
  const [isShowAllPaymentModal, setIsShowAllPaymentModal] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isShowConfirmSendEmailModal, setIsShowConfirmSendEmailModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamQuoteDetail = {
      quoteId: quoteId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetDetailQuote(params);
  }, []);

  useEffect(() => {
    if (!isRefreshQuoteDetail) return;

    const params: IQueryParamQuoteDetail = {
      quoteId: quoteId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetDetailQuote(params);

    dispatch(quoteActions.setRefreshQuoteDetail(false));
  }, [isRefreshQuoteDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailQuote = (params: IQueryParamQuoteDetail) => {
    setIsLoading(true);

    dispatch(getDetailQuote(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        if (!data) return;

        setDetailQuote(data as IQuoteDetail);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDataTableOrderProduct = (
    orderItems: IQuoteItem[],
    orderItemAddOns: IQuoteAddOn[],
    quoteSubRentals: IQuoteSubRental[] = []
  ) => {
    const orderItemMap = orderItems.reduce<Record<string, IQuoteItem>>((acc, item) => {
      acc[item?.id || EMPTY_STRING] = item;
      return acc;
    }, {});

    const subRentalMap = quoteSubRentals.reduce<Record<string, IQuoteSubRental>>((acc, item) => {
      acc[item.inventoryId] = item;
      return acc;
    }, {});

    const addOnMap = orderItemAddOns.reduce<Record<string, IProductQuoteItem[]>>((acc, addOn) => {
      const parent = orderItemMap[addOn?.parentId || EMPTY_STRING];
      const parentType = parent ? parent.type : EMPTY_STRING;

      const subRental = subRentalMap[addOn.inventoryId];

      const displayName = subRental
        ? `${addOn.name} / ${t('form_quotes_title_sub_rental')} / ${subRental.quantity} - ${formatCurrencyUSD({
            value: subRental.price,
            showDecimal: true,
          })}`
        : addOn.name;

      if (!acc[addOn?.parentId || EMPTY_STRING]) acc[addOn?.parentId || EMPTY_STRING] = [];

      acc[addOn?.parentId || EMPTY_STRING].push({
        ...addOn,
        parentType,
        name: displayName,
      } as IProductQuoteItem);

      return acc;
    }, {});

    const result: IProductQuoteItem[] = [];

    for (const item of orderItems) {
      const subRental = subRentalMap[item.inventoryId];

      result.push({
        ...item,
        name: subRental
          ? `${item.name} / ${t('form_quotes_title_sub_rental')} / ${subRental.quantity} - ${formatCurrencyUSD({
              value: subRental.price,
              showDecimal: true,
            })}`
          : item.name,
      } as IProductQuoteItem);

      const addOns = addOnMap[item?.id || EMPTY_STRING];
      if (addOns && addOns.length > 0) {
        result.push(...addOns);
      }
    }

    return result;
  };

  const handleShowPaymentModal = () => {
    setIsShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
  };

  const handleShowAllPaymentModal = () => {
    setIsShowAllPaymentModal(true);
  };

  const handleCloseAllPaymentModal = () => {
    setIsShowAllPaymentModal(false);
  };

  const handleSavePayment = async (body: IPaymentQuote) => {
    setIsLoading(true);

    const convertData: IPaymentQuote = {
      ...body,
      businessId: quoteId,
      businessType: BusinessTypeEnum.QUOTE,
    };

    dispatch(createPayment(convertData))
      .unwrap()
      .then((_res) => {
        dispatch(quoteActions.setRefreshQuoteDetail(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
        handleClosePaymentModal();
      });
  };

  const generatePdfBlob = async (data: IQuotePdf): Promise<Blob | null> => {
    try {
      return await pdf(<QuotePdf quotePdf={data} />).toBlob();
    } catch (error) {
      console.error('Generate PDF failed: ', error);
      return null;
    }
  };

  const handleSendEmail = async () => {
    if (!detailQuote || !quoteId) return;

    const data: IQuotePdf = {
      client: detailQuote?.client,
      poNumber: detailQuote?.poNumber || EMPTY_STRING,
      startDate: detailQuote?.startDate || EMPTY_STRING,
      startTime: detailQuote?.startTime || EMPTY_STRING,
      endDate: detailQuote?.endDate || EMPTY_STRING,
      endTime: detailQuote?.endTime || EMPTY_STRING,
      quoteItems: detailQuote?.quoteItems || [],
      quoteAddOns: detailQuote?.quoteAddOns || [],
      deliveryCost: detailQuote?.deliveryCost || EMPTY_STRING,
      damageWaiver: detailQuote?.damageWaiver || EMPTY_STRING,
      laborFee: detailQuote?.laborFee || EMPTY_STRING,
      discountRate: detailQuote?.discountRate || EMPTY_STRING,
      depositMadeAmount: detailQuote?.depositMadeAmount || EMPTY_STRING,
      couponAmount: detailQuote?.couponAmount || EMPTY_STRING,
      customFees: detailQuote?.quoteCustom,
      productSubtotal: detailQuote?.productSubtotal || EMPTY_STRING,
      total: detailQuote?.total || EMPTY_STRING,
      taxAmount: detailQuote?.taxAmount || EMPTY_STRING,
      mileage: detailQuote?.mileage || EMPTY_STRING,
      externalNote: detailQuote?.externalNote || EMPTY_STRING,
      internalNote: detailQuote?.internalNote || EMPTY_STRING,
      dataProduct:
        handleDataTableOrderProduct(
          detailQuote?.quoteItems || [],
          detailQuote?.quoteAddOns || [],
          detailQuote?.quoteSubRentals || []
        ) || [],
    };

    const pdfBlob = await generatePdfBlob(data);

    const formData = new FormData();

    if (!pdfBlob) return;

    formData.append('boldSignFile', pdfBlob, DEFAULT_QUOTE_FILE_NAME_PDF);
    formData.append('invoicePdf', pdfBlob, DEFAULT_INVOICE_FILE_NAME_PDF);

    setIsLoading(true);

    const payload: IBodyUpdate<FormData> = {
      id: quoteId,
      body: formData,
    };

    dispatch(sendMailQuote(payload))
      .unwrap()
      .then((_res) => {})
      .catch(() => {})
      .finally(() => {
        handleShowConfirmSendEmailModal();
        setIsLoading(false);
      });
  };

  const handleShowConfirmSendEmailModal = () => {
    setIsShowConfirmSendEmailModal(!isShowConfirmSendEmailModal);
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePrintQuote = async () => {
    if (!detailQuote || !quoteId) return;

    const data: IQuotePdf = {
      client: detailQuote?.client,
      poNumber: detailQuote?.poNumber || EMPTY_STRING,
      startDate: detailQuote?.startDate || EMPTY_STRING,
      startTime: detailQuote?.startTime || EMPTY_STRING,
      endDate: detailQuote?.endDate || EMPTY_STRING,
      endTime: detailQuote?.endTime || EMPTY_STRING,
      quoteItems: detailQuote?.quoteItems || [],
      quoteAddOns: detailQuote?.quoteAddOns || [],
      deliveryCost: detailQuote?.deliveryCost || EMPTY_STRING,
      damageWaiver: detailQuote?.damageWaiver || EMPTY_STRING,
      laborFee: detailQuote?.laborFee || EMPTY_STRING,
      discountRate: detailQuote?.discountRate || EMPTY_STRING,
      depositMadeAmount: detailQuote?.depositMadeAmount || EMPTY_STRING,
      couponAmount: detailQuote?.couponAmount || EMPTY_STRING,
      customFees: detailQuote?.quoteCustom,
      productSubtotal: detailQuote?.productSubtotal || EMPTY_STRING,
      total: detailQuote?.total || EMPTY_STRING,
      taxAmount: detailQuote?.taxAmount || EMPTY_STRING,
      mileage: detailQuote?.mileage || EMPTY_STRING,
      externalNote: detailQuote?.externalNote || EMPTY_STRING,
      internalNote: detailQuote?.internalNote || EMPTY_STRING,
      dataProduct:
        handleDataTableOrderProduct(
          detailQuote?.quoteItems || [],
          detailQuote?.quoteAddOns || [],
          detailQuote?.quoteSubRentals || []
        ) || [],
    };

    const pdfBlob = await generatePdfBlob(data);

    if (pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob);
      const newTab = window.open(url, '_blank', 'noopener,noreferrer');
      if (newTab) newTab.opener = null;

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };
  //#endregion Handle Function

  return (
    <Stack id='infoQuoteTabComponent' className={cx('container')} spacing={4}>
      <InformationQuote detailQuote={detailQuote} />

      <Grid2 spacing={4} container>
        <Grid2 direction={'column'} size={3}>
          <TimeLineActionQuote
            onSendEmail={handleShowConfirmSendEmailModal}
            detailQuote={detailQuote}
            onPrintQuote={handlePrintQuote}
            onMakePayment={handleShowPaymentModal}
            handleShowAllPaymentModal={handleShowAllPaymentModal}
          />
        </Grid2>

        <Grid2 direction={'column'} size={9}>
          <Stack gap={4} mb={4} className={cx('quoteDetails')}>
            <BaseLabel
              styleType='title'
              label={t('quote_detail_title_quote_details')}
              icon={icons.commonIconQuoteItems}
            />

            <BaseTable
              dataSource={
                handleDataTableOrderProduct(
                  detailQuote?.quoteItems || [],
                  detailQuote?.quoteAddOns || [],
                  detailQuote?.quoteSubRentals || []
                ) || []
              }
              columns={columns(t)}
            />
          </Stack>

          <EventDetailsQuoteDetail detailQuote={detailQuote} />

          <EventTineLineQuoteDetail detailQuote={detailQuote} />

          <NoteQuoteDetail detailQuote={detailQuote} />

          <FeesQuoteDetail detailQuote={detailQuote} />

          <TotalInfoQuoteDetail detailQuote={detailQuote} onMakePayment={handleShowPaymentModal} />
        </Grid2>
      </Grid2>

      {isLoading && <Loading />}

      {isShowPaymentModal && detailQuote && (
        <FormPaymentQuote quoteDetail={detailQuote} onClose={handleClosePaymentModal} onSubmit={handleSavePayment} />
      )}

      {isShowAllPaymentModal && (
        <PaymentListModal
          quoteDetail={detailQuote}
          onClose={handleCloseAllPaymentModal}
          type={TypeInventoryGroupDay.QUOTE}
        />
      )}

      {isShowConfirmSendEmailModal && (
        <ConfirmModal
          type='default'
          icon={icons.commonIconEmail}
          title={t('order_detail_send_mail', { ns: 'admin/order' })}
          onClose={handleShowConfirmSendEmailModal}
          onConfirm={handleSendEmail}
        />
      )}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Stack>
  );
};

export default InfoQuoteTab;
